import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pause, Play, Settings2, Square } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { SUPPORTED_LOCALES, toBcp47Locale, type SupportedLocale } from '../locales/locale';
import {
  cycleDocumentSpeechRate,
  filterDocumentSpeechVoicesForLang,
  formatDocumentSpeechRateLabel,
  getDocumentSpeechPreferences,
  getDocumentSpeechStatus,
  isDocumentSpeechBarVisible,
  isDocumentSpeechSupported,
  listDocumentSpeechVoices,
  pauseDocumentSpeech,
  resumeDocumentSpeech,
  setDocumentSpeechPreferences,
  stopDocumentSpeech,
  subscribeDocumentSpeech,
  type DocumentSpeechPreferences,
  type DocumentSpeechStatus,
} from '../utils/document-speech';
import { Button } from './ui/button';
import { SingleSelectCombobox } from './ui/single-select-combobox';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';

export const DocumentSpeechPlaybackBar: React.FC<{
  documentId?: string | null;
}> = ({ documentId = null }) => {
  const { t } = useLocale();
  const [status, setStatus] = useState<DocumentSpeechStatus>(() => getDocumentSpeechStatus());
  const [preferences, setPreferences] = useState<DocumentSpeechPreferences>(() =>
    getDocumentSpeechPreferences(),
  );
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>(() => listDocumentSpeechVoices());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      stopDocumentSpeech();
    };
  }, [documentId]);

  useEffect(() => {
    if (!isDocumentSpeechSupported()) {
      return;
    }
    return subscribeDocumentSpeech((state) => {
      setStatus(state.status);
      setPreferences(state.preferences);
    });
  }, []);

  useEffect(() => {
    if (!isDocumentSpeechSupported()) {
      return;
    }
    const refreshVoices = (): void => {
      setVoices(listDocumentSpeechVoices());
    };
    refreshVoices();
    window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', refreshVoices);
    };
  }, []);

  useEffect(() => {
    if (!settingsOpen) {
      return;
    }
    const handlePointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (barRef.current?.contains(target)) {
        return;
      }
      const comboboxPortal = target instanceof Element
        ? target.closest('[data-radix-popper-content-wrapper], [role="listbox"]')
        : null;
      if (comboboxPortal) {
        return;
      }
      setSettingsOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [settingsOpen]);

  const languageOptions = useMemo(
    () =>
      SUPPORTED_LOCALES.map((locale: SupportedLocale) => ({
        value: toBcp47Locale(locale),
        label: t(`locale_name_${locale}`),
      })),
    [t],
  );

  const voiceOptions = useMemo(() => {
    const matching = filterDocumentSpeechVoicesForLang(voices, preferences.lang);
    return [
      { value: '', label: t('speech_voice_automatic') },
      ...matching.map((voice) => ({
        value: voice.voiceURI,
        label: `${voice.name} (${voice.lang})`,
      })),
    ];
  }, [voices, preferences.lang, t]);

  if (!isDocumentSpeechSupported() || !isDocumentSpeechBarVisible()) {
    return null;
  }

  const canResume = status === 'paused' || status === 'ended';
  const ratePercent = Math.round(preferences.rate * 100);
  const volumePercent = Math.round(preferences.volume * 100);

  return createPortal(
    <TooltipProvider>
      <div
        ref={barRef}
        data-testid="document-speech-playback-bar"
        className="fixed bottom-4 left-1/2 z-[100] flex w-[min(22rem,calc(100vw-1.5rem))] -translate-x-1/2 flex-col gap-2"
        role="region"
        aria-label={t('reading_aloud')}
      >
        {settingsOpen ? (
          <div
            data-testid="document-speech-settings"
            className="space-y-3 rounded-lg border border-border bg-card p-3 text-card-foreground shadow-lg"
          >
            <div className="space-y-1.5">
              <label className="flex items-center justify-between text-xs font-medium text-foreground">
                <span>{t('speech_rate')}</span>
                <span className="tabular-nums text-muted-foreground">{ratePercent}%</span>
              </label>
              <input
                type="range"
                min={50}
                max={200}
                step={5}
                value={ratePercent}
                aria-label={t('speech_rate')}
                className="w-full accent-foreground"
                onChange={(event) => {
                  setDocumentSpeechPreferences({
                    rate: Number(event.target.value) / 100,
                  });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center justify-between text-xs font-medium text-foreground">
                <span>{t('speech_volume')}</span>
                <span className="tabular-nums text-muted-foreground">{volumePercent}%</span>
              </label>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={volumePercent}
                aria-label={t('speech_volume')}
                className="w-full accent-foreground"
                onChange={(event) => {
                  setDocumentSpeechPreferences({
                    volume: Number(event.target.value) / 100,
                  });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">{t('speech_language')}</label>
              <SingleSelectCombobox
                options={languageOptions}
                value={preferences.lang}
                onValueChange={(value) => {
                  setDocumentSpeechPreferences({ lang: value, voiceURI: null });
                }}
                placeholder={t('speech_language')}
                searchPlaceholder={t('speech_search')}
                emptyMessage={t('speech_no_voices')}
                className="w-full"
                contentClassName="z-[120]"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-foreground">{t('speech_voice')}</label>
              <SingleSelectCombobox
                options={voiceOptions}
                value={preferences.voiceURI ?? ''}
                onValueChange={(value) => {
                  setDocumentSpeechPreferences({ voiceURI: value || null });
                }}
                placeholder={t('speech_voice')}
                searchPlaceholder={t('speech_search')}
                emptyMessage={t('speech_no_voices')}
                className="w-full"
                contentClassName="z-[120]"
              />
            </div>
          </div>
        ) : null}
        <div className="flex items-center gap-1 self-center rounded-lg border border-border bg-card/95 px-2 py-1.5 text-card-foreground shadow-lg backdrop-blur-sm">
          <span className="hidden px-2 text-xs font-medium text-muted-foreground sm:inline">
            {t('reading_aloud')}
          </span>
          <ButtonTooltip
            tooltip={canResume ? t('resume_reading') : t('pause_reading')}
            side="top"
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={canResume ? t('resume_reading') : t('pause_reading')}
              onClick={() => {
                if (canResume) {
                  resumeDocumentSpeech();
                } else {
                  pauseDocumentSpeech();
                }
              }}
            >
              {canResume ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </Button>
          </ButtonTooltip>
          <ButtonTooltip tooltip={t('stop_reading')} side="top">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={t('stop_reading')}
              onClick={() => {
                stopDocumentSpeech();
              }}
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
          </ButtonTooltip>
          <ButtonTooltip tooltip={t('speech_rate')} side="top">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 min-w-[2.75rem] px-2 text-xs font-medium tabular-nums"
              aria-label={t('speech_rate')}
              data-testid="document-speech-rate-cycle"
              onClick={() => {
                cycleDocumentSpeechRate();
              }}
            >
              {formatDocumentSpeechRateLabel(preferences.rate)}
            </Button>
          </ButtonTooltip>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={t('speech_settings')}
            aria-expanded={settingsOpen}
            title={t('speech_settings')}
            onClick={() => {
              setSettingsOpen((open) => !open);
            }}
          >
            <Settings2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </TooltipProvider>,
    document.body,
  );
};
